import { useState } from "react";
import { Filter, RotateCcw, X } from "lucide-react";
import { DATE_MODES, DATE_PRESETS, FILTER_FIELDS, presetRange } from "@/lib/marketing";

const Select = ({ label, value, onChange, children, testid }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">
      {label}
    </span>
    <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
      {children}
    </select>
  </label>
);

/** One filter bar drives every KPI, funnel, table and export on the page. */
export const FilterBar = ({ filters, options, onChange, onReset }) => {
  const [open, setOpen] = useState(false);
  const set = (patch) => onChange({ ...filters, ...patch });
  const chips = Object.entries(filters).filter(
    ([k, v]) => v && !["date_from", "date_to", "date_mode", "preset", "campaign"].includes(k));

  const body = (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <Select label="Date range" value={filters.preset} testid="filter-preset"
        onChange={(v) => set({ preset: v, ...presetRange(v) })}>
        {DATE_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      </Select>
      <Select label="Date mode" value={filters.date_mode} testid="filter-date-mode"
        onChange={(v) => set({ date_mode: v })}>
        {DATE_MODES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
      </Select>
      {FILTER_FIELDS.map((f) => (
        <Select key={f.key} label={f.label} value={filters[f.key]}
          testid={`filter-${f.key}`} onChange={(v) => set({ [f.key]: v })}>
          <option value="">All</option>
          {(f.values || options?.[f.options] || []).map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </Select>
      ))}
    </div>
  );

  return (
    <section data-testid="campaign-filter-bar"
      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
          <Filter className="h-4 w-4" /> Filters
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="filter-reset" onClick={onReset}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300
              px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50">
            <RotateCcw className="h-3 w-3" /> Reset filters
          </button>
          <button data-testid="filter-drawer-toggle" onClick={() => setOpen((v) => !v)}
            className="lg:hidden rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">
            {open ? "Close" : "Filters"}
          </button>
        </div>
      </div>

      {chips.length > 0 && (
        <div data-testid="filter-chips" className="flex flex-wrap gap-2 mt-3">
          {chips.map(([k, v]) => (
            <button key={k} data-testid={`filter-chip-${k}`} onClick={() => set({ [k]: "" })}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 border
                border-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-700
                hover:bg-slate-200">
              {k.replace(/_/g, " ")}: {v} <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      <div className="hidden lg:block mt-4">{body}</div>
      {open && <div data-testid="filter-drawer" className="lg:hidden mt-4">{body}</div>}
    </section>
  );
};
